import { getContractorReliability, RELIABILITY_CONFIG, type ReliabilityTier } from "@/data/contractorReliability";
import type { ContractorScorecard } from "@/hooks/useContractorScorecards";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface Props {
  scorecard: ContractorScorecard;
  size?: "sm" | "md";
  showTooltip?: boolean;
}

export function ReliabilityBadge({ scorecard, size = "sm", showTooltip = true }: Props) {
  const { tier, score, factors } = getContractorReliability(scorecard);
  const config = RELIABILITY_CONFIG[tier];
  const Icon = config.icon;

  const badge = (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-bold ${config.bg} ${config.color} ${config.border} ${
      size === "sm" ? "text-[10px]" : "text-xs"
    }`}>
      <Icon className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} />
      {tier}
    </span>
  );

  if (!showTooltip) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[220px]">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider">Reliability Score</span>
            <span className={`text-xs font-bold ${config.color}`}>{score}/100</span>
          </div>
          {factors.map((f) => (
            <div key={f.label} className="flex items-center justify-between text-[10px]">
              <span className="text-muted-foreground">{f.label}</span>
              <span className={`font-medium ${
                f.impact === "positive" ? "text-success" : f.impact === "negative" ? "text-destructive" : "text-muted-foreground"
              }`}>{f.value}</span>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
